import { ApiRequestError } from '../../lib/api';

/**
 * What the sign-in screens say when something stops a sign-in.
 *
 * Every notice is two parts: what happened, and **one** thing to do next. The
 * screens used to take the API's `userMessage` as the title and add their own
 * next step beneath it, and the API's messages carry next steps of their own —
 * so a correct password followed by a request for the code read "Enter the
 * 6-digit code from your authenticator app to finish signing in. Check the
 * address and password, or reset your password below." Three instructions, and
 * the last two were wrong: the password was right, which is why the code was
 * asked for at all.
 *
 * So the titles here state facts and never instruct, and `nextStep` is a
 * single instruction. `sign-in.spec.tsx` renders every state and checks both
 * halves of that, rather than trusting this comment.
 *
 * `AUTH_MFA_CODE_REQUIRED` is deliberately absent. It is not a failure: the
 * screen moves to its code step instead of showing a notice.
 */
export interface SignInNotice {
  title: string;
  nextStep: string;
}

const NOTICES: Partial<Record<string, SignInNotice>> = {
  AUTH_INVALID_CREDENTIALS: {
    title: 'That email and password did not match an account.',
    nextStep: 'Check both and try again.',
  },
  AUTH_ACCOUNT_LOCKED: {
    title: 'This account is locked for 15 minutes after too many failed attempts.',
    nextStep: 'Wait 15 minutes, then sign in again.',
  },
  AUTH_RATE_LIMITED: {
    title: 'There have been too many sign-in attempts in the last minute.',
    nextStep: 'Wait a minute, then try again.',
  },
  // The password was right. There is no screen on which an account can enrol
  // an authenticator before it has signed in, and no administrator screen that
  // enrols one for somebody else, so "set one up" would send the person
  // looking for something that is not there.
  AUTH_MFA_REQUIRED: {
    title:
      'Your password is right, but your role needs an authenticator app and none is set up for this account.',
    nextStep: 'Contact Home Care support to have one set up.',
  },
  AUTH_MFA_INVALID: {
    title: 'That code did not match.',
    nextStep: 'Enter the code your authenticator app is showing now.',
  },
};

const OFFLINE: SignInNotice = {
  title: 'Home Care could not be reached.',
  nextStep: 'Check your connection and try again.',
};

const UNEXPECTED: SignInNotice = {
  title: 'Something on our side stopped this sign-in.',
  nextStep: 'Try again in a moment.',
};

export function signInNotice(error: unknown): SignInNotice {
  if (!(error instanceof ApiRequestError)) return UNEXPECTED;
  if (error.envelope.requestId === 'offline') return OFFLINE;
  return NOTICES[error.code] ?? UNEXPECTED;
}

/** The API is asking for the second factor: move to the code step. */
export const asksForCode = (error: unknown): boolean =>
  error instanceof ApiRequestError && error.code === 'AUTH_MFA_CODE_REQUIRED';

/**
 * Notices on the agency picker, where the selection may already be spent.
 *
 * Every refusal there except "enter the code" ends the selection — the API
 * spends it, and the list on screen no longer works. The next step is
 * therefore always to start the sign-in again, and the screen offers it as
 * the one button rather than as a sentence beside a list that has stopped
 * responding.
 */
const PICKER: Partial<Record<string, SignInNotice>> = {
  AUTH_SELECTION_TOKEN_INVALID: {
    title: 'This list of agencies has expired or was already used.',
    nextStep: 'Sign in again to get a new one.',
  },
  AUTH_SELECTION_AGENCY_NOT_A_CANDIDATE: {
    title: 'That agency is not available for this sign-in.',
    nextStep: 'Sign in again to get a new list.',
  },
  AUTH_MFA_INVALID: {
    title: 'That code did not match, so this sign-in has ended.',
    nextStep: 'Sign in again and enter the code your app is showing then.',
  },
};

export function pickerNotice(error: unknown): SignInNotice {
  if (error instanceof ApiRequestError) {
    const own = PICKER[error.code];
    if (own) return own;
  }
  return signInNotice(error);
}
